import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, X, Edit2, Trash2, Calendar, Clock, ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import JoditEditor from 'jodit-react';

const API_BASE_URL = "https://backend.dlapakistan.com/api";
const API_ENDPOINTS = {
  DENTAL_NEWS_DETAIL: (id) => `${API_BASE_URL}/dental-news/${id}/`,
  DENTAL_NEWS_POSTS: `${API_BASE_URL}/dental-news-posts/`,
  DENTAL_NEWS_POSTS_BY_NEWS: (newsId) => `${API_BASE_URL}/dental-news-posts/by_dental_news/?dental_news_id=${newsId}`,
  DENTAL_NEWS_POST_DETAIL: (id) => `${API_BASE_URL}/dental-news-posts/${id}/`,
  TOGGLE_POST_PUBLISH: (id) => `${API_BASE_URL}/dental-news-posts/${id}/toggle_publish/`,
};

const DlaNewsPosts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  
  const [parentNews, setParentNews] = useState(null);
  const [newsPosts, setNewsPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    dental_news: id,
    title: "",
    description: "",
    is_published: true
  });

  const emerald600 = "#059669";

  // Jodit Editor Configuration - USING useMemo TO PREVENT RECREATION
  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Write your post description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Axios instance
  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com",
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 10000,
  });

  // Load data on initial render
  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load parent news data
      if (id) {
        const newsResponse = await api.get(API_ENDPOINTS.DENTAL_NEWS_DETAIL(id));
        setParentNews(newsResponse.data);
        
        // Load posts for this news
        await fetchPosts();
      } else {
        // Try to get from localStorage as fallback
        const savedNews = localStorage.getItem('current_dental_news');
        if (savedNews) {
          const newsData = JSON.parse(savedNews);
          setParentNews(newsData);
          await fetchPosts();
        } else {
          setError("No dental news selected. Please select a news item first.");
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data. Please try again.");
      showErrorAlert("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const currentNewsId = id || (parentNews && parentNews.id);
      if (!currentNewsId) return;
      
      const response = await api.get(API_ENDPOINTS.DENTAL_NEWS_POSTS_BY_NEWS(currentNewsId));
      
      // CHANGED HERE: Sort posts by created_at date (oldest first)
      const sortedPosts = response.data.sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      
      setNewsPosts(sortedPosts);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setNewsPosts([]);
    }
  };

  const closeModal = () => {
    if (saving) return;
    
    setShowModal(false);
    setEditingPost(null);
    setForm({
      dental_news: id,
      title: "",
      description: "",
      is_published: true
    });
    
    // Clear editor reference
    if (editorRef.current) {
      editorRef.current = null;
    }
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonColor: emerald600,
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2.5'
      }
    });
  };

  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: emerald600,
      confirmButtonText: 'OK',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
  };

  const showDeleteConfirmation = (postId, postTitle) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `<div class="text-left">
        <p class="mb-2">You are about to delete the news post:</p>
        <p class="font-bold text-gray-800">"${postTitle}"</p>
        <p class="mt-3 text-sm text-gray-600">This action cannot be undone.</p>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2.5',
        cancelButton: 'rounded-lg px-6 py-2.5'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteConfirmed(postId, postTitle);
      }
    });
  };

  const handleDeleteConfirmed = async (postId, postTitle) => {
    try {
      await api.delete(API_ENDPOINTS.DENTAL_NEWS_POST_DETAIL(postId));
      
      // Update local state
      setNewsPosts(prev => prev.filter(post => post.id !== postId));
      
      Swal.fire({
        title: 'Deleted!',
        html: `<div class="text-left">
          <p class="mb-2">The news post has been deleted:</p>
          <p class="font-bold text-gray-800">"${postTitle}"</p>
        </div>`,
        icon: 'success',
        confirmButtonColor: emerald600,
        confirmButtonText: 'OK',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } catch (err) {
      console.error("Error deleting post:", err);
      showErrorAlert("Failed to delete news post. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!form.title.trim()) {
      showErrorAlert("Please enter a title for the news post.");
      return;
    }

    if (form.title.trim().length < 1) {
      showErrorAlert("Post title must be at least 1 character long.");
      return;
    }

    if (!form.description.trim()) {
      showErrorAlert("Please add description for the news post.");
      return;
    }

    if (form.description.trim().length < 10) {
      showErrorAlert("Description must be at least 10 characters long.");
      return;
    }

    setSaving(true);

    try {
      const postData = {
        dental_news: id,
        title: form.title.trim(),
        description: form.description.trim(),
        is_published: form.is_published
      };

      if (editingPost) {
        // Update existing post
        const response = await api.put(
          API_ENDPOINTS.DENTAL_NEWS_POST_DETAIL(editingPost.id),
          postData
        );
        
        // Update local state
        setNewsPosts(prev => 
          prev.map(post => 
            post.id === editingPost.id ? response.data : post
          )
        );
        
        showSuccessAlert("News post updated successfully!");
      } else {
        // Add new post
        const response = await api.post(
          API_ENDPOINTS.DENTAL_NEWS_POSTS,
          postData
        );
        
        // Update local state
        setNewsPosts(prev => [response.data, ...prev]);
        showSuccessAlert("News post added successfully!");
      }

      closeModal();
    } catch (err) {
      console.error("Error saving post:", err);
      
      if (err.response && err.response.data) {
        if (err.response.data.title) {
          showErrorAlert(Array.isArray(err.response.data.title) 
            ? err.response.data.title.join(' ') 
            : err.response.data.title);
        } else if (err.response.data.description) {
          showErrorAlert(Array.isArray(err.response.data.description) 
            ? err.response.data.description.join(' ') 
            : err.response.data.description);
        } else if (err.response.data.error) {
          showErrorAlert(err.response.data.error);
        } else {
          showErrorAlert("Failed to save news post. Please try again.");
        }
      } else {
        showErrorAlert("Failed to save news post. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({
      dental_news: post.dental_news || id,
      title: post.title,
      description: post.description,
      is_published: post.is_published
    });
    setShowModal(true);
  };

  const handleDelete = (postId) => {
    const post = newsPosts.find(post => post.id === postId);
    if (post) {
      showDeleteConfirmation(postId, post.title);
    }
  };

  const handleAddNew = () => {
    setEditingPost(null);
    setForm({
      dental_news: id,
      title: "",
      description: "",
      is_published: true
    });
    setShowModal(true);
  };

  const togglePublishStatus = async (postId, currentStatus, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await api.patch(
        API_ENDPOINTS.TOGGLE_POST_PUBLISH(postId)
      );
      
      // Update local state
      setNewsPosts(prev => 
        prev.map(post => 
          post.id === postId 
            ? { ...post, is_published: response.data.is_published }
            : post
        )
      );
      
      showSuccessAlert(response.data.message);
    } catch (err) {
      console.error("Error toggling publish status:", err);
      showErrorAlert("Failed to update publish status. Please try again.");
    }
  };

  const handleEditorChange = (newContent) => {
    setForm(prev => ({
      ...prev,
      description: newContent
    }));
  };

  const calculateReadTime = (content) => {
    // Remove HTML tags for word count
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return minutes || 1;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || "Unknown date";
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid time";
      }
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Unknown time";
    }
  };

  const goBack = () => {
    navigate('/dental-news');
  };

  if (isLoading) {
    return (
      <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left flex-1">
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={goBack}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 rounded-lg transition-all duration-200 font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {parentNews ? parentNews.title : "DLA News Posts"}
            </h1>
            <p className="text-gray-600 mt-1">
              {parentNews 
                ? `Manage posts for: ${parentNews.title}`
                : "Create and manage news posts"
              }
            </p>
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">{error}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleAddNew}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
          >
            <Plus className="w-5 h-5 mr-2" /> Add Post
          </button>
        </div>
      </div>

      {/* CONTENT AREA - ALL POSTS DISPLAYED IN SPONSORSHIP STYLE */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {!parentNews ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-400 text-lg mb-2">No news item selected</p>
            <p className="text-gray-400 text-sm">Please select a news item from the Dental News page first</p>
            <button
              onClick={goBack}
              style={{ backgroundColor: emerald600 }}
              className="mt-6 flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all mx-auto"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Go to Dental News
            </button>
          </div>
        ) : newsPosts.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-400 text-lg mb-2">No posts yet for "{parentNews.title}"</p>
            <p className="text-gray-400 text-sm">Click "Add Post" to create your first post for this news</p>
            <button
              onClick={handleAddNew}
              style={{ backgroundColor: emerald600 }}
              className="mt-6 flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all mx-auto"
            >
              <Plus className="w-5 h-5 mr-2" /> Create First Post
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {newsPosts.map((post) => (
              <div key={post.id} className="animate-fadeIn w-full bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                
                {/* TOP SECTION: TITLE AND ACTIONS ON RIGHT */}
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  
                  {/* TITLE AND METADATA ON LEFT */}
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="text-left">
                        {/* Publish Status Badge */}
                        <div className="flex items-center gap-3 mb-3">
                          <button
                            onClick={(e) => togglePublishStatus(post.id, post.is_published, e)}
                            className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                              post.is_published 
                                ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' 
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            } transition-colors`}
                            title={post.is_published ? "Published - Click to unpublish" : "Unpublished - Click to publish"}
                          >
                            {post.is_published ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Published
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                Unpublished
                              </>
                            )}
                          </button>
                        </div>
                        
                        {/* Post Title */}
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                          {post.title || "Untitled Post"}
                        </h2>
                        
                        {/* DATE, TIME AND READ TIME INFO */}
                        <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm mt-2">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{formatDate(post.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{formatTime(post.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{calculateReadTime(post.description)} min read</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* ACTION BUTTONS */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(post)}
                          className="flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg font-medium hover:bg-emerald-100 transition-all hover:shadow-sm active:scale-95"
                        >
                          <Edit2 className="w-4 h-4 mr-2" /> 
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition-all hover:shadow-sm active:scale-95"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> 
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* DESCRIPTION BELOW - Full width */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div 
                    className="text-gray-700 leading-relaxed custom-description w-full text-left" 
                    dangerouslySetInnerHTML={{ __html: post.description || "" }} 
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL FOR ADDING/EDITING POSTS - SAME STYLE */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingPost ? "Edit Post" : "Add New Post"}
                  <div className="text-sm font-normal text-gray-600 mt-1">
                    For: {parentNews?.title || "Unknown News"}
                  </div>
                </h2>
                <button 
                  onClick={closeModal} 
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving}
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                
                {/* TITLE */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Post Title <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
                    placeholder="Enter post title here..." 
                    required 
                    disabled={saving}
                    maxLength={200}
                    autoFocus
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500 ml-1">
                      Minimum 1 character
                    </p>
                    <p className={`text-xs ${form.title.length === 200 ? 'text-red-500' : 'text-gray-500'}`}>
                      {form.title.length}/200 characters
                    </p>
                  </div>
                </div>

                {/* DESCRIPTION - Rich text editor */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.description}
                      config={editorConfig}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== form.description) {
                          setForm(prev => ({...prev, description: newContent}));
                        }
                      }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500 ml-1">
                      Use the toolbar for rich text formatting
                    </p>
                    <p className={`text-xs ${form.description.length > 5000 ? 'text-red-500' : 'text-gray-500'}`}>
                      Content length: {form.description.length} characters
                    </p>
                  </div>
                </div>

                {/* Publish Status */}
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_published}
                      onChange={(e) => setForm({...form, is_published: e.target.checked})}
                      disabled={saving}
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-gray-700 font-medium">
                      Publish this post
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    Published posts will be visible to users
                  </p>
                </div>
                
                {/* SUBMIT BUTTON */}
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 active:brightness-95 transition-all duration-200 text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {editingPost ? 'Updating...' : 'Creating...'}
                    </>
                  ) : editingPost ? (
                    <>
                      <Edit2 className="w-5 h-5 mr-2" />
                      Update Post
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5 mr-2" />
                      Create Post
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        /* Clean, simple text styling - LEFT ALIGNED */
        .custom-description * { 
          background-color: transparent !important; 
          border: none !important; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description {
          text-align: left;
        }
        
        .custom-description p {
          font-size: 1.125rem;
          line-height: 1.75;
          color: #374151;
          text-align: left;
        }
        
        .custom-description h1, 
        .custom-description h2, 
        .custom-description h3, 
        .custom-description h4, 
        .custom-description h5, 
        .custom-description h6 {
          font-weight: bold;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #111827;
          text-align: left;
        }
        
        .custom-description h1 { font-size: 2.25rem; }
        .custom-description h2 { font-size: 1.875rem; }
        .custom-description h3 { font-size: 1.5rem; }
        .custom-description h4 { font-size: 1.25rem; }
        .custom-description h5 { font-size: 1.125rem; }
        .custom-description h6 { font-size: 1rem; }
        
        .custom-description ul { 
          list-style-type: disc; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description ol { 
          list-style-type: decimal; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description li {
          margin-bottom: 0.5rem;
          text-align: left;
        }
        
        .custom-description strong, 
        .custom-description b {
          font-weight: bold;
        }
        
        .custom-description em, 
        .custom-description i {
          font-style: italic;
        }
        
        .custom-description u {
          text-decoration: underline;
        }
        
        .custom-description a {
          color: #059669;
          text-decoration: underline;
        }
        
        .custom-description a:hover {
          color: #047857;
        }
        
        .custom-description blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #6b7280;
          text-align: left;
        }
        
        .custom-description img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
        
        .custom-description table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          text-align: left;
        }
        
        .custom-description table th,
        .custom-description table td {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          text-align: left;
        }
        
        .custom-description table th {
          background-color: #f9fafb;
          font-weight: bold;
        }
        
        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 300px;
          border-radius: 16px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-radius: 16px 16px 0 0;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 20px !important;
          min-height: 200px;
          border-radius: 0 0 16px 16px;
        }
      `}</style>
    </div>
  );
};

export default DlaNewsPosts;